import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps,
} from "recharts";
import { EdaResults, DistributionAnalysis, EdaFeatureStats } from "../../types";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

interface FeatureDistributionChartProps {
  edaResults: EdaResults | null;
  initialFeature?: string;
}

// Helper function to prepare histogram data for a single feature
const prepareHistogramData = (analysis: DistributionAnalysis | undefined) => {
  if (!analysis || !analysis.histogram_bins || !analysis.histogram_edges) {
    return [];
  }
  const { histogram_bins, histogram_edges } = analysis;
  return histogram_bins.map((count, index) => ({
    name: `${histogram_edges[index].toFixed(2)} - ${histogram_edges[index + 1].toFixed(2)}`,
    Anzahl: count,
    binStart: histogram_edges[index],
    binEnd: histogram_edges[index + 1],
  }));
};

// --- Custom Tooltip Component Definition (remains mostly the same) ---
const CustomTooltipContent = (
  { active, payload, label }: TooltipProps<ValueType, NameType>,
  featureStats: EdaFeatureStats | undefined,
  analysis: DistributionAnalysis | undefined,
) => {
  if (
    active &&
    payload &&
    payload.length &&
    featureStats &&
    analysis?.quartiles
  ) {
    const data = payload[0].payload; // Access the data of the hovered bar
    return (
      <div className="custom-tooltip">
        <p className="label">{`Bin: ${data.name}`}</p>
        <p className="intro">{`Anzahl: ${payload[0].value}`}</p>
        <hr />
        <p className="desc">
          <strong>Statistiken (Gesamtfeature):</strong>
        </p>
        {/* Access stats from the passed props */}
        <p>{`Mean: ${featureStats.mean?.toFixed(2) ?? "N/A"}`}</p>
        <p>{`Median: ${analysis.quartiles.median.toFixed(2)}`}</p>
        <p>{`Q1: ${analysis.quartiles.q1.toFixed(2)}`}</p>
        <p>{`Q3: ${analysis.quartiles.q3.toFixed(2)}`}</p>
        <p>{`Ausreißer: ${analysis.outlier_count ?? 0} (${analysis.outlier_percentage?.toFixed(1) ?? 0}%)`}</p>
      </div>
    );
  }
  return null;
};

// --- Wrapper function to create the tooltip content creator ---
const createTooltipContent =
  (
    featureStats: EdaFeatureStats | undefined,
    analysis: DistributionAnalysis | undefined,
  ) =>
  (props: TooltipProps<ValueType, NameType>) =>
    CustomTooltipContent(props, featureStats, analysis);

export const FeatureDistributionChart: React.FC<
  FeatureDistributionChartProps
> = ({ edaResults, initialFeature }) => {
  // State for the selected feature
  const [selectedFeature, setSelectedFeature] = useState<string>(
    initialFeature || "",
  );

  // --- Hooks moved to the top ---
  // Memoize numeric feature list and related data
  const { numericFeatures, distributionAnalysis, featuresStats } =
    useMemo(() => {
      const features = edaResults?.features_stats;
      const analysisData = edaResults?.distribution_analysis;
      return {
        numericFeatures: features?.numeric_feature_names || [],
        distributionAnalysis: analysisData,
        featuresStats: features?.stats_per_feature,
      };
    }, [edaResults]);

  // Get data for the currently selected feature
  const analysis = useMemo(
    () => distributionAnalysis?.[selectedFeature],
    [distributionAnalysis, selectedFeature],
  );
  const featureStats = useMemo(
    () => featuresStats?.[selectedFeature],
    [featuresStats, selectedFeature],
  );
  const chartData = useMemo(() => prepareHistogramData(analysis), [analysis]);
  const tooltipContent = useMemo(
    () => createTooltipContent(featureStats, analysis),
    [featureStats, analysis],
  );

  // Calculate domain including outlier bounds
  const finalDomain = useMemo((): [number, number] => {
    // Check data validity *inside* useMemo, but call the hook unconditionally
    const isDataValidForDomain =
      !!analysis && !analysis.message && !!featureStats && chartData.length > 0;
    if (!isDataValidForDomain || !analysis?.histogram_edges || !featureStats)
      return [0, 1]; // Default domain

    const xMin = analysis.histogram_edges[0];
    const xMax = analysis.histogram_edges[analysis.histogram_edges.length - 1];
    const lowerBound = analysis.outlier_bounds?.lower ?? xMin;
    const upperBound = analysis.outlier_bounds?.upper ?? xMax;

    const domainMin = Math.min(
      xMin,
      analysis.quartiles?.q1 ?? xMin,
      featureStats.mean ?? xMin,
      lowerBound,
    );
    const domainMax = Math.max(
      xMax,
      analysis.quartiles?.q3 ?? xMax,
      featureStats.mean ?? xMax,
      upperBound,
    );

    const domainPadding = (domainMax - domainMin) * 0.05;
    // Handle case where min and max are the same
    if (domainMax === domainMin) {
      return [domainMin - 1, domainMax + 1]; // Example padding for single value
    }
    return [domainMin - domainPadding, domainMax + domainPadding];
  }, [analysis, featureStats, chartData]); // Dependencies updated
  // --- End of moved hooks ---

  // Effect to set the initial selected feature when data loads or changes
  useEffect(() => {
    if (numericFeatures.length > 0 && !selectedFeature) {
      setSelectedFeature(numericFeatures[0]);
    } else if (
      numericFeatures.length > 0 &&
      !numericFeatures.includes(selectedFeature)
    ) {
      // If current selection is no longer valid (e.g., new data loaded), reset
      setSelectedFeature(numericFeatures[0]);
    } else if (numericFeatures.length === 0) {
      setSelectedFeature(""); // Clear selection if no numeric features
    }
  }, [numericFeatures, selectedFeature]);

  const handleFeatureChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFeature(event.target.value);
  };

  // Initial checks (can now happen after hooks)
  if (!edaResults || !distributionAnalysis || !featuresStats) {
    // Render placeholder or null if essential data is missing entirely
    return (
      <div className="chart-container card distribution-chart">
        <h3>Feature Verteilung</h3>
        <p>Unvollständige EDA Daten für Verteilungsanalyse vorhanden.</p>
      </div>
    );
  }

  if (numericFeatures.length === 0) {
    return (
      <div className="chart-container card distribution-chart">
        <h3>Feature Verteilung</h3>
        <p>Keine numerischen Features für Verteilungsanalyse gefunden.</p>
      </div>
    );
  }

  // Check for selected feature data validity (used for rendering chart or placeholder)
  const isSelectedDataValid =
    !!analysis && !analysis.message && !!featureStats && chartData.length > 0;

  return (
    <div className="chart-container card distribution-chart">
      <div className="chart-header-controls">
        {" "}
        {/* Header for title and dropdown */}
        <h3>Feature Verteilung</h3>
        <div className="feature-selector">
          <label htmlFor={`feature-select-${selectedFeature}`}>Feature:</label>
          <select
            id={`feature-select-${selectedFeature}`}
            value={selectedFeature}
            onChange={handleFeatureChange}
            disabled={numericFeatures.length <= 1}
          >
            {numericFeatures.map((feature) => (
              <option key={feature} value={feature}>
                {feature}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedFeature ? (
        <p>Bitte wählen Sie ein Feature aus.</p>
      ) : !isSelectedDataValid ? (
        <p>
          Keine gültigen Verteilungsdaten für "{selectedFeature}" verfügbar.
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 40, // Increased right margin for outlier labels
                left: 20,
                bottom: 60,
              }}
              barCategoryGap="0%"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="binStart"
                type="number"
                scale="linear"
                domain={finalDomain}
                tickFormatter={(tick) => tick.toFixed(1)}
                angle={-45}
                textAnchor="end"
                height={70}
                label={{
                  value: selectedFeature,
                  position: "insideBottom",
                  dy: 40,
                }}
              />
              <YAxis
                label={{ value: "Anzahl", angle: -90, position: "insideLeft" }}
                allowDecimals={false}
              />
              <Tooltip content={tooltipContent} />
              <Bar dataKey="Anzahl" fill="#82ca9d" />

              {/* --- Reference Lines (Mean, Median, Quartiles) --- */}
              {featureStats.mean !== undefined && (
                <ReferenceLine
                  x={featureStats.mean}
                  stroke="#e91e63"
                  strokeWidth={2}
                  label={{
                    value: `Mean: ${featureStats.mean.toFixed(2)}`,
                    position: "insideTopRight",
                    fill: "#e91e63",
                    fontSize: 10,
                  }}
                />
              )}
              {analysis.quartiles?.median !== undefined && (
                <ReferenceLine
                  x={analysis.quartiles.median}
                  stroke="#ff9800"
                  strokeWidth={2}
                  label={{
                    value: `Median: ${analysis.quartiles.median.toFixed(2)}`,
                    position: "insideTopLeft",
                    fill: "#ff9800",
                    fontSize: 10,
                  }}
                />
              )}
              {analysis.quartiles?.q1 !== undefined && (
                <ReferenceLine
                  x={analysis.quartiles.q1}
                  stroke="#9c27b0"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  label={{
                    value: `Q1: ${analysis.quartiles.q1.toFixed(2)}`,
                    position: "bottom",
                    fill: "#9c27b0",
                    fontSize: 10,
                  }}
                />
              )}
              {analysis.quartiles?.q3 !== undefined && (
                <ReferenceLine
                  x={analysis.quartiles.q3}
                  stroke="#9c27b0"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  label={{
                    value: `Q3: ${analysis.quartiles.q3.toFixed(2)}`,
                    position: "bottom",
                    fill: "#9c27b0",
                    fontSize: 10,
                  }}
                />
              )}

              {/* --- Reference Lines (Outlier Bounds) --- */}
              {analysis.outlier_bounds?.lower !== undefined && (
                <ReferenceLine
                  x={analysis.outlier_bounds.lower}
                  stroke="#757575"
                  strokeDasharray="4 2"
                  strokeWidth={1}
                  label={{
                    value: `Ausreißer unten: ${analysis.outlier_bounds.lower.toFixed(2)}`,
                    position: "top",
                    fill: "#757575",
                    fontSize: 9,
                    dy: -5,
                  }}
                />
              )}
              {analysis.outlier_bounds?.upper !== undefined && (
                <ReferenceLine
                  x={analysis.outlier_bounds.upper}
                  stroke="#757575"
                  strokeDasharray="4 2"
                  strokeWidth={1}
                  label={{
                    value: `Ausreißer oben: ${analysis.outlier_bounds.upper.toFixed(2)}`,
                    position: "top",
                    fill: "#757575",
                    fontSize: 9,
                    dy: -5,
                  }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
          {analysis.outlier_count !== undefined &&
            analysis.outlier_percentage !== undefined && (
              <div className="outlier-info">
                Ausreißer: {analysis.outlier_count} (
                {analysis.outlier_percentage.toFixed(1)}%)
              </div>
            )}
        </>
      )}
    </div>
  );
};
